<script setup>
import { useQuality } from '@/composables/useQuality'
import { useAbility } from '@casl/vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'Capa',
  },
})

const route = useRoute('quality-capa-id')
const router = useRouter()
const ability = useAbility()
const { capa, loading, saving, fetchCapa, submitCapaApproval, closeCapa } = useQuality()

const load = () => fetchCapa(route.params.id)

onMounted(load)

// TaskList.vue tidak meng-emit event apa pun saat task dibuat/diselesaikan
// (murni internal state), jadi CapaCloseGuard di-refresh manual lewat
// tombol "Muat Ulang" pada guard-nya sendiri (defineExpose refresh()) atau
// navigasi ulang ke halaman ini — bukan real-time otomatis. BR-010 tetap
// DITEGAKKAN penuh di backend terlepas dari kapan tampilan guard ini
// diperbarui, hanya soal freshness tampilan.
const closeGuard = ref(null)
const actionError = ref('')

const doSubmitApproval = async () => {
  actionError.value = ''
  try {
    await submitCapaApproval(route.params.id)
    await load()
  } catch (e) {
    actionError.value = e?.data?.message ?? 'Gagal mengajukan approval.'
  }
}

const doClose = async () => {
  actionError.value = ''
  try {
    await closeCapa(route.params.id)
    await load()
  } catch (e) {
    actionError.value = e?.data?.message ?? 'CAPA tidak bisa ditutup.'
  }
}

const statusColor = value => ({ draft: 'secondary', open: 'info', closed: 'success' }[value] ?? 'secondary')
</script>

<template>
  <div>
    <div class="d-flex align-center gap-3 mb-4">
      <VBtn
        icon="ri-arrow-left-line"
        variant="text"
        @click="router.push({ name: 'quality-capa' })"
      />
      <div class="flex-grow-1">
        <div class="d-flex align-center gap-3">
          <h4 class="text-h4 font-weight-bold mb-0">
            {{ capa?.title }}
          </h4>
          <VChip
            v-if="capa"
            size="small"
            :color="statusColor(capa.status)"
            variant="tonal"
          >
            {{ capa.status }}
          </VChip>
        </div>
      </div>
    </div>

    <VProgressLinear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <template v-else-if="capa">
      <VAlert
        v-if="actionError"
        type="error"
        variant="tonal"
        density="compact"
        class="mb-4"
      >
        {{ actionError }}
      </VAlert>

      <VRow>
        <VCol
          cols="12"
          md="8"
        >
          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0">
              Deskripsi
            </VCardTitle>
            <VCardText class="pa-6">
              <p class="text-body-2 mb-2">
                {{ capa.description }}
              </p>
              <p
                v-if="capa.root_cause"
                class="text-body-2 text-medium-emphasis mb-0"
              >
                <strong>Akar Masalah:</strong> {{ capa.root_cause }}
              </p>
            </VCardText>
          </VCard>

          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0">
              Approval
            </VCardTitle>
            <VCardText class="pa-6">
              <VBtn
                v-if="capa.status === 'draft' && ability.can('create', 'Capa')"
                :loading="saving"
                class="mb-4"
                @click="doSubmitApproval"
              >
                Ajukan Approval
              </VBtn>
              <ApprovalStepper
                approvable-type="capa"
                :approvable-id="capa.id"
              />
            </VCardText>
          </VCard>

          <VCard class="mb-4">
            <VCardTitle class="pa-6 pb-0">
              Tindak Lanjut (Task)
            </VCardTitle>
            <VCardText class="pa-6">
              <CapaCloseGuard
                ref="closeGuard"
                :capa-id="capa.id"
              />
              <div class="d-flex justify-end mb-2">
                <VBtn
                  size="small"
                  variant="text"
                  prepend-icon="ri-refresh-line"
                  @click="closeGuard?.refresh()"
                >
                  Muat Ulang Status Task
                </VBtn>
              </div>
              <TaskList
                taskable-type="capa"
                :taskable-id="capa.id"
                scope="all"
              />
            </VCardText>
          </VCard>

          <VCard>
            <VCardTitle class="pa-6 pb-0">
              Evidence
            </VCardTitle>
            <VCardText class="pa-6">
              <EvidenceUploader
                evidenceable-type="capa"
                :evidenceable-id="capa.id"
              />
            </VCardText>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          md="4"
        >
          <VCard v-if="capa.status !== 'closed' && ability.can('close', 'Capa')">
            <VCardText class="pa-6">
              <VBtn
                block
                color="success"
                :loading="saving"
                :disabled="closeGuard && !closeGuard.canClose"
                @click="doClose"
              >
                Tutup CAPA
              </VBtn>
              <p class="text-caption text-medium-emphasis mt-2 mb-0">
                Hanya bisa ditutup setelah seluruh Task selesai (BR-010).
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </template>
  </div>
</template>
